import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  previewPlanterInvite,
  acceptPlanterInvite,
  linkExistingPlanterInvite,
  PlanterInvitePreview,
} from '../api/planterInviteService';

/**
 * Landing page for email links: /planter-invite?token=...
 * Completes account setup in the browser or deep-links to the mobile app.
 */
const PlanterInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [preview, setPreview] = useState<PlanterInvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [linking, setLinking] = useState(false);
  const existingToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) {
      setLoadError('This invitation link is missing a token. Use the full link from your email.');
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await previewPlanterInvite(token);
        if (!cancelled) {
          setPreview(data);
        }
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e as Error)?.message ||
          'This invitation is invalid or has expired.';
        if (!cancelled) {
          setLoadError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const appDeepLink = token ? `twonetwork://planter-invite?token=${encodeURIComponent(token)}` : '';

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!token) return;
    const inviteEmail = preview?.email?.trim().toLowerCase();
    if (!inviteEmail) {
      setSubmitError('Could not load your invitation email. Refresh the page or use the link from your email.');
      return;
    }
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await acceptPlanterInvite({
        token,
        email: inviteEmail,
        password,
      });
      if (res.user?.token) {
        localStorage.setItem('token', res.user.token);
        if (res.user.refreshToken) {
          localStorage.setItem('refreshToken', res.user.refreshToken);
        }
        localStorage.setItem(
          'user',
          JSON.stringify({
            id: res.user.id,
            email: res.user.email,
            firstName: res.user.firstName,
            lastName: res.user.lastName,
            isEmailVerified: false,
            roles: res.user.roles || ['church_planter'],
          })
        );
        window.location.assign('/dashboard');
      } else {
        setSubmitError('Account created but no session was returned. Try signing in.');
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response
        ?.status;
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Could not complete invitation.';
      if (status === 409 || code === 'USER_EXISTS') {
        setSubmitError(
          `${message} After you sign in with that email, open this same invitation link again to link your planter profile, or use Redeem planter invitation in the app.`
        );
      } else {
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkExisting = async () => {
    if (!token) return;
    setSubmitError(null);
    setLinking(true);
    try {
      const res = await linkExistingPlanterInvite(token);
      if (res.user?.token) {
        localStorage.setItem('token', res.user.token);
        if (res.user.refreshToken) {
          localStorage.setItem('refreshToken', res.user.refreshToken);
        }
        localStorage.setItem(
          'user',
          JSON.stringify({
            id: res.user.id,
            email: res.user.email,
            firstName: res.user.firstName,
            lastName: res.user.lastName,
            isEmailVerified: false,
            roles: res.user.roles || ['church_planter'],
          })
        );
        window.location.assign('/dashboard');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Could not link invitation.';
      setSubmitError(message);
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img className="mx-auto h-24 w-auto" src="/logo.png" alt="242Network" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            You&apos;re set up to submit reports
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Set a password to access your monthly ministry report.
          </p>
        </div>

        {loadingPreview && (
          <p className="text-center text-gray-600">Loading invitation…</p>
        )}

        {loadError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
        )}

        {preview && !loadError && (
          <>
            <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium">{preview.planterName}</p>
              <p className="mt-1 text-blue-800 break-all">
                Email: {preview.email || preview.emailMasked}
              </p>
            </div>

            {existingToken && (
              <div className="rounded-md border border-gray-200 p-4">
                <p className="text-sm text-gray-700 mb-3">
                  You are signed in. Link this invitation to your current account if the email matches.
                </p>
                <button
                  type="button"
                  onClick={handleLinkExisting}
                  disabled={linking}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {linking ? 'Linking…' : 'Link invitation to my signed-in account'}
                </button>
              </div>
            )}

            <form className="mt-6 space-y-6" onSubmit={handleAccept}>
              <div>
                <label htmlFor="invite-password" className="block text-sm font-medium text-gray-700">
                  Create password
                </label>
                <input
                  id="invite-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              {submitError && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {submitting ? 'Creating account…' : 'Set password & open report'}
              </button>
            </form>

            <div className="pt-4 border-t border-gray-200 text-center text-xs text-gray-500 space-y-2">
              {appDeepLink && (
                <p>
                  <a href={appDeepLink} className="text-primary-600 hover:text-primary-500 font-medium">
                    Open in 242 Network app
                  </a>
                  <span className="text-gray-400"> · </span>
                  <span>
                    Or install the app and use &quot;Redeem planter invitation&quot; on sign-in.
                  </span>
                </p>
              )}
              <p>
                <Link to="/login" className="text-gray-500 hover:text-gray-700 underline">
                  Already have an account? Sign in
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanterInvite;
